import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { History, ChevronDown, ChevronRight, PlayCircle } from "lucide-react";

const fmtP = (n) =>
  n == null ? "—" : Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
const fmt2 = (n) => Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (s) => {
  if (!s) return "";
  try { return new Date(s).toLocaleString(undefined, { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return s; }
};

export const ExecutionHistory = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [result, setResult] = useState("all"); // all | win | loss
  const [symbol, setSymbol] = useState("all");

  useEffect(() => {
    let active = true;
    api
      .get("/matches/history/executions")
      .then(({ data }) => {
        if (!active) return;
        const ms = data.matches || [];
        setMatches(ms);
        if (ms[0]) setOpen({ [ms[0].match_id]: true });
      })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const actLabel = {
    open: t("act_open"), close: t("act_close"), close_partial: t("act_close_partial"),
    add: t("act_add"), flip: t("act_flip"), auto: t("act_auto"),
  };
  const isExit = (a) => a === "close" || a === "close_partial";
  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));

  if (loading) return null;

  const symbolOptions = Array.from(new Set(matches.map((m) => m.symbol)));
  const filtered = matches.filter((m) => {
    if (result === "win" && m.profit < 0) return false;
    if (result === "loss" && m.profit >= 0) return false;
    if (symbol !== "all" && m.symbol !== symbol) return false;
    return true;
  });
  const resultTabs = [
    { key: "all", label: t("filter_all") },
    { key: "win", label: t("filter_wins") },
    { key: "loss", label: t("filter_losses") },
  ];

  return (
    <div data-testid="exec-history">
      <h2 className="font-heading font-bold text-xl mb-4 flex items-center gap-2">
        <History className="w-5 h-5 text-signal" /> {t("exec_history")}
      </h2>
      {matches.length === 0 ? (
        <div className="font-mono text-sm text-white/40 py-4 border border-white/10 bg-panel px-4" data-testid="exec-empty">
          {t("no_exec")}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <div className="flex gap-1 border border-white/10 rounded-sm p-0.5 bg-panel">
              {resultTabs.map((tab) => (
                <button
                  key={tab.key}
                  data-testid={`exec-filter-${tab.key}`}
                  onClick={() => setResult(tab.key)}
                  className={`px-3 py-1.5 font-mono text-xs uppercase tracking-wide rounded-sm transition-colors ${
                    result === tab.key ? "bg-signal text-base font-bold" : "text-white/50 hover:text-white/80"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <select
              data-testid="exec-filter-symbol"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value)}
              className="bg-panel border border-white/10 rounded-sm px-3 py-1.5 font-mono text-xs text-white/80 focus:outline-none focus:border-signal/50"
            >
              <option value="all">{t("all_instruments")}</option>
              {symbolOptions.map((sym) => (
                <option key={sym} value={sym}>{sym === "???" ? t("anon_label") : sym}</option>
              ))}
            </select>
          </div>
          {filtered.length === 0 ? (
            <div className="font-mono text-sm text-white/40 py-4 border border-white/10 bg-panel px-4" data-testid="exec-filtered-empty">
              {t("no_exec")}
            </div>
          ) : (
          <div className="space-y-2">
          {filtered.map((m, gi) => {
            const up = m.profit >= 0;
            const isOpen = !!open[m.match_id];
            return (
              <div key={m.match_id} className="border border-white/10 bg-panel" data-testid={`exec-group-${gi}`}>
                <div className="w-full flex items-center gap-3 px-4 py-3 font-mono text-xs hover:bg-panelhover transition-colors">
                  <button
                    data-testid={`exec-group-toggle-${gi}`}
                    onClick={() => toggle(m.match_id)}
                    className="flex-1 flex items-center gap-3 text-left"
                  >
                    {isOpen ? <ChevronDown className="w-4 h-4 text-white/40" /> : <ChevronRight className="w-4 h-4 text-white/40" />}
                    <span className="text-white/70">{fmtDate(m.created_at)}</span>
                    <span className="text-white/50">{m.symbol}</span>
                    <span className="text-white/30">· {m.trades} exec</span>
                    <span className="flex-1 text-right text-white/40 uppercase tracking-widest">{t("profit")}</span>
                  </button>
                  <span className={`w-28 text-right font-bold tabular-nums ${up ? "text-profit" : "text-loss"}`} data-testid={`exec-group-pnl-${gi}`}>
                    {up ? "+" : ""}{fmt2(m.profit)}
                  </span>
                  {m.finished && (
                    <button
                      data-testid={`exec-review-${gi}`}
                      onClick={() => navigate(`/results/${m.match_id}`)}
                      className="flex items-center gap-1 px-2.5 py-1 border border-signal/40 text-signal hover:bg-signal/10 rounded-sm uppercase tracking-wide transition-colors whitespace-nowrap"
                    >
                      <PlayCircle className="w-3.5 h-3.5" /> {t("review_match")}
                    </button>
                  )}
                </div>
                {isOpen && (
                  <div className="border-t border-white/5 divide-y divide-white/5">
                    {m.executions.map((r, i) => (
                      <div key={`${r.step}-${r.action}-${r.amount}-${i}`} data-testid={`exec-row-${gi}-${i}`} className="flex items-center gap-3 px-4 py-2 pl-11 font-mono text-xs">
                        <span className={`w-24 uppercase tracking-wide ${isExit(r.action) ? "text-loss" : "text-profit"}`}>
                          {actLabel[r.action] || r.action}
                        </span>
                        <span className={`w-12 ${r.side === "buy" ? "text-profit" : "text-loss"}`}>{(r.side || "").toUpperCase()}</span>
                        <span className="flex-1 text-right text-white/50">
                          {t("amount")}: <span className="text-white/80 tabular-nums">{fmt2(r.amount)}</span>
                        </span>
                        <span className="w-32 text-right text-white/50">
                          {t("col_price")}: <span className="text-white tabular-nums" data-testid={`exec-price-${gi}-${i}`}>{fmtP(r.price)}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
          )}
        </>
      )}
    </div>
  );
};
